// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ObservableList } from '@Organisms/Observable/ObservableList/ObservableList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import { ObjectTypeV1 as ObjectType, ObservableOutput, AnalyzerOutput, CaseOutput } from 'thehive-sdk';
import client from '@Client';
import ListProvider from '@Context/ListProvider';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { useLocalMessage } from '@Helpers/locale';
import { useStreamObserver } from '@Hooks/useStream';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { useGenericCountQuery, useGenericQuery } from '@Queries/genericQueries';
import { ShortcutPin } from '@Molecules/Pin';
import useConfigList from '@Hooks/useConfigList';
import { computeEventList as observableEventList } from '@Organisms/Observable/eventList';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { THLayout } from '@Molecules/THLayout';
import { computeListSelector } from '@Organisms/Observable/selector';
import { ICONS } from '@Constants/iconsApp';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector } from '@Organisms/Case/selector';
import usePageTitle from '@Hooks/usePageTitle';

const CaseObservablesList = (): ReactElement => {
    const i = useLocalMessage();
    const { caseId } = useParams<{ caseId: string }>();

    const { data: analyzerData } = useGenericQuery<AnalyzerOutput[]>({
        fetchCallback: () => client.AnalyzerController.listAnalyzer(),
        key: ['get-all-analyzer'],
    });

    const analyzer = {
        data: analyzerData,
        onFinish: (items: any[]): Promise<void> =>
            client.AnalyzerController.multipleExecuteJob(items)
                .then(() => {
                    openMessage('success', i('analyzer.launch.success'));
                })
                .catch(() => {
                    openMessage('success', i('analyzer.launch.error'));
                }),
    };

    const config = useConfigList<ObservableOutput>({
        selector: computeListSelector({ caseId }),
        controller: client.ObservableController,
        storageId: StorageFilterEnum.CASE_OBSERVABLE,
        objectType: ObjectType.OBSERVABLE,
        streamRootId: caseId,
    });

    const { listRefetch, countRefetch } = config;

    const eventList = useMemo(
        () =>
            observableEventList({
                controller: client.ObservableController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (count, message) => {
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
            }),
        [countRefetch, i, listRefetch],
    );

    const { data: caseData } = useOneQuery<CaseOutput>({
        selector: computeGetSelector({ caseId }),
        controller: client.CaseController,
        manual: true,
        fetchOnStart: false,
    });

    usePageTitle(i('page-title.cases.observables', { number: caseData?.number || 0 }));

    return (
        <THLayout>
            <THLayout.Content>
                <ObservableList
                    config={config}
                    eventList={eventList}
                    analyzer={analyzer}
                    caseId={caseId}
                    permissions={caseData?.userPermissions}
                />
            </THLayout.Content>
        </THLayout>
    );
};

const MenuCount = (): ReactElement => {
    const { caseId } = useParams<{ caseId: string }>();
    const local = useLocalMessage();
    const history = useHistory();

    const selector = useMemo(() => computeListSelector({ caseId }), [caseId]);

    const { data: count = 0, refetch: countRefetch } = useGenericCountQuery({
        controller: client.CaseController,
        queryParams: {
            selector,
        },
        fetchOnStart: true,
    });

    const processedCount = useMemo(() => (count < 0 ? `+${Math.abs(count)}` : count), [count]);

    useStreamObserver(
        { rootId: caseId, objectType: STREAM_EVENTS.OBSERVABLE },
        (data): void => {
            const successFound = data.some((action) => ['create', 'update', 'delete'].includes(action.base.action));
            if (successFound || data.length === 0) countRefetch();
        },
        [countRefetch],
    );

    return (
        <>
            <ICONS.ObservableIcon />
            <span className="tab-counter__label">
                {local('case.layout.nav-bar.observables')} ({processedCount})
            </span>
            <ShortcutPin
                caseId={caseId}
                type="observables"
                onClick={({ url }: { url: string }): void => history.push(url)}
            />
        </>
    );
};

export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.observables',
        render: <MenuCount />,
    },
    path: RouteBuilder.compute('cases.observables'),
    content: <CaseObservablesList />,
    Context: ListProvider,
    exact: true,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.observables' },
};
